"use strict";

/**
 * Statische Hilfsklasse zum Überprüfen von Annahmen zu Variablen.
 * Alle öffentlichen Methoden geben 'this' zurück (chainability).
 *
 * @class
 * @static
 * @memberof gc.gis
 * @author Anton Popov <a.popov@brain-scc.de>
 */
gc.gis.assert = {

	/**
	 * Wirft einen Fehler, falls {@link gc.gis.GCApplication#DEBUG} aktiv und
	 * die gegebene Bedinung nicht erfüllt ist.
	 *
	 * @private
	 * @method
	 * @param {boolean} condition Bedingung
	 * @param {string}  message   (optional) auszugebende Nachricht
	 * @return {Object} this
	 * @throws {gc.gis.error.GCAssertionError}
	 */
	__assert: function( condition, message )
	{
		if( gc.gis.iface.DEBUG && !condition )
		{
			throw new gc.gis.error.GCAssertionError( message );
		}
		return this;
	},

	/**
	 * Überprüft, ob der gegebene Wert definiert ist.
	 * Wirft einen Fehler, falls nicht und wenn
	 * {@link gc.gis.GCApplication#DEBUG} aktiv ist.
	 *
	 * @public
	 * @method
	 * @param {*} value Wert
	 * @return {Object} this
	 * @throws {gc.gis.error.GCAssertionError}
	 */
	isSet: function( value )
	{
		return this.__assert(
			typeof value !== 'undefined',
			'Undefined variable'
		);
	},

	/**
	 * Überprüft, ob der gegebene Wert <code>null</code> ist.
	 * Wirft einen Fehler, falls nicht und wenn
	 * {@link gc.gis.GCApplication#DEBUG} aktiv ist.
	 *
	 * @public
	 * @method
	 * @param {*} value Wert
	 * @return {Object} this
	 * @throws {gc.gis.error.GCAssertionError}
	 */
	notNull: function( value )
	{
		return this.__assert( value !== null, 'Variable is null');
	},

	/**
	 * Überprüft, ob der gegebene Wert eine Zahl ist.
	 * Wirft einen Fehler, falls nicht und wenn
	 * {@link gc.gis.GCApplication#DEBUG} aktiv ist.
	 *
	 * @public
	 * @method
	 * @param {*} value Wert
	 * @return {Object} this
	 * @throws {gc.gis.error.GCAssertionError}
	 */
	isNumber: function( value )
	{
		return this.__assert(
			typeof value === 'number',
			'Not a number: '+ value
		);
	},

	/**
	 * Überprüft, ob der gegebene Wert ein boolscher Wert ist.
	 * Wirft einen Fehler, falls nicht und wenn
	 * {@link gc.gis.GCApplication#DEBUG} aktiv ist.
	 *
	 * @public
	 * @method
	 * @param {*} value Wert
	 * @return {Object} this
	 * @throws {gc.gis.error.GCAssertionError}
	 */
	isBool: function( value )
	{
		return this.__assert(
			value === true || value === false,
			'Not a boolean: '+ value
		);
	},

	/**
	 * Überprüft, ob der gegebene Wert eine Instanz einer Klasse ist.
	 * Wirft einen Fehler, falls nicht und wenn
	 * {@link gc.gis.GCApplication#DEBUG} aktiv ist.
	 *
	 * @public
	 * @method
	 * @param {*}        value Wert
	 * @param {Function} type  Klasse
	 * @return {Object} this
	 * @throws {gc.gis.error.GCAssertionError}
	 */
	isInstance: function( value, type )
	{
		this.isSet( type );

		return this.__assert(
			value instanceof type,
			'['+ value +'] is not an instance of ['+ type +']'
		);
	},

	/**
	 * Überprüft, ob der gegebene Wert ein Array ist.
	 * Wirft einen Fehler, falls nicht und wenn
	 * {@link gc.gis.GCApplication#DEBUG} aktiv ist.
	 *
	 * Führt {@link gc.gis.assert.isNumber} aus.
	 *
	 * @public
	 * @method
	 * @param {*} value Wert
	 * @return {Object} this
	 * @throws {gc.gis.error.GCAssertionError}
	 */
	isArray: function( value )
	{
		return this.__assert(
			value && this.isNumber( value.length ),
			'Not an array: '+ value
		);
	},

	/**
	 * Überprüft, ob der gegebene Wert innerhalb der gegebenen Grenzen liegt.
	 * Wirft einen Fehler, falls nicht und wenn
	 * {@link gc.gis.GCApplication#DEBUG} aktiv ist.
	 *
	 * Führt {@link gc.gis.assert.isNumber} aus.
	 *
	 * @public
	 * @method
	 * @param {*}      value Wert
	 * @param {number} min   Minimaler Wert
	 * @param {number} max   Maximaler Wert
	 * @return {Object} this
	 * @throws {gc.gis.error.GCAssertionError}
	 */
	inRange: function( value, min, max )
	{
		this.isNumber( value ).isNumber( min ).isNumber( max );

		this.__assert( value >= min, 'Value ['+ value +'] below minimum ['+ min +']');
		this.__assert( value <= max, 'Value ['+ value +'] beyond maximum ['+ max +']');

		return this;
	},

	/**
	 * Überprüft, ob der gegebene Wert ein nicht leeres Array ist.
	 * Wirft einen Fehler, falls nicht und wenn
	 * {@link gc.gis.GCApplication#DEBUG} aktiv ist.
	 *
	 * Führt {@link gc.gis.assert.isArray} aus.
	 *
	 * @public
	 * @method
	 * @param {*} value Wert
	 * @return {Object} this
	 * @throws {gc.gis.error.GCAssertionError}
	 */
	notEmpty: function( value )
	{
		this.isArray( value );

		return this.__assert( value.length > 0, 'Array is empty');
	},

	/**
	 * Überprüft, ob der gegebene Wert ein leeres Array ist.
	 * Wirft einen Fehler, falls nicht und wenn
	 * {@link gc.gis.GCApplication#DEBUG} aktiv ist.
	 *
	 * Führt {@link gc.gis.assert.isArray} aus.
	 *
	 * @public
	 * @method
	 * @param {*} value Wert
	 * @return {Object} this
	 * @throws {gc.gis.error.GCAssertionError}
	 */
	isEmpty: function( value )
	{
		this.isArray( value );

		return this.__assert( value.length <= 0, 'Array is not empty');
	}

};
